import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from "react";
import { useQuery as defaultUseQuery, useMutation as defaultUseMutation, } from "@apollo/react-hooks";
import { GlobalStylesProvider, Modal, ModalContent, } from "@zapier/design-system";
import { useMaybeSendFullStoryEvent } from "@zapier/toolbox-tracking";
import { DropletonLegacyMigrationOfferModalView, IntroPricingOfferModalView, } from "@zapier/promotions/esm/components/campaigns";
import { campaignSlugs } from "@zapier/promotions/esm/utils";
import { useOfferModal as _useOfferModal } from "./hooks";
import { MultiIntervalOfferModalView, OfferAcceptedView, OfferErrorView, SingleIntervalOfferModalView, } from "./views";
const getFullStoryEventName = (campaignName) => {
    let name = "Viewed Offer Modal";
    if (campaignName) {
        name += ` - ${campaignName}`;
    }
    return name;
};
const MODAL_STATE = {
    error: "ERROR",
    offer: "OFFER",
    offerAccepted: "OFFER_ACCEPTED",
};
const DEFAULT_MODAL_WIDTH = "630px";
const defaultState = {
    activeView: MODAL_STATE.offer,
    error: {
        body: undefined,
        title: undefined,
    },
};
export const OfferModal = ({ offerId, onClose = () => {
}, useOfferModal = _useOfferModal, ...otherProps }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const useQuery = (_a = otherProps === null || otherProps === void 0 ? void 0 : otherProps.useQuery) !== null && _a !== void 0 ? _a : defaultUseQuery;
    const useMutation = (_b = otherProps === null || otherProps === void 0 ? void 0 : otherProps.useMutation) !== null && _b !== void 0 ? _b : defaultUseMutation;
    const [state, setState] = useState(defaultState);
    const onAcceptOfferSuccess = () => setState({
        activeView: MODAL_STATE.offerAccepted,
        error: { body: null, title: null },
    });
    const { acceptOffer, accountCurrency, allowIntervalSelection, error, isLoading, isOfferAvailable, isPaymentMethodProvided, isSaving, offer, offerEndDate, promoPlans, showModal, } = useOfferModal({ offerId, onAcceptOfferSuccess, useQuery, useMutation });
    const [isOpen, setIsOpen] = useState(true);
    const campaignName = getFullStoryEventName((_d = (_c = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _c === void 0 ? void 0 : _c.campaign) === null || _d === void 0 ? void 0 : _d.name);
    const shouldShowModal = showModal && isOpen && !isLoading;
    useMaybeSendFullStoryEvent(shouldShowModal && !!campaignName, campaignName);
    const closeModal = () => {
        setIsOpen(false);
        onClose();
    };
    if (!shouldShowModal) {
        return null;
    }
    const wrapContent = (content, isSubmitting = false) => {
        const props = {
            accountCurrency,
            acceptOffer,
            error,
            isPaymentMethodProvided,
            useQuery,
            useMutation,
        };
        if (allowIntervalSelection) {
            const monthlyPlan = promoPlans.find((plan) => plan.interval === "month");
            const yearlyPlan = promoPlans.find((plan) => plan.interval === "year");
            return (monthlyPlan &&
                yearlyPlan && (_jsx(MultiIntervalOfferModalView, { ...props, isSubmitting: isSubmitting, monthlyIntervalAmount: monthlyPlan.amount, monthlyPlanId: monthlyPlan.id, onCancel: closeModal, yearlyIntervalAmount: yearlyPlan.amount, yearlyPlanId: yearlyPlan.id, children: content })));
        }
        return (_jsx(SingleIntervalOfferModalView, { ...props, isSubmitting: isSubmitting, onCancel: closeModal, children: content }));
    };
    const campaignSlug = (_g = (_f = (_e = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _e === void 0 ? void 0 : _e.campaign) === null || _f === void 0 ? void 0 : _f.slug) !== null && _g !== void 0 ? _g : "";
    const isOfferAcceptedView = state.activeView === MODAL_STATE.offerAccepted;
    const getContent = () => {
        var _a, _b;
        const destinationPlan = (_a = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _a === void 0 ? void 0 : _a.destinationPlan;
        switch (true) {
            case campaignSlug.startsWith(campaignSlugs.INTRO_STARTER): {
                if (!offer || !destinationPlan || !offerEndDate) {
                    break;
                }
                return wrapContent(_jsx(IntroPricingOfferModalView, { accountCurrency: accountCurrency, destinationPlanAmount: destinationPlan.amount, destinationPlanName: (_b = destinationPlan.displayShortName) !== null && _b !== void 0 ? _b : destinationPlan.name, maxUses: offer.variant.campaign.maxUses, offerEndDate: offerEndDate, totalCost: offer.variant.totalCost }), isSaving);
            }
            case campaignSlug.startsWith(campaignSlugs.DROPLETON_LEGACY_MIGRATION): {
                if (!offerEndDate || !destinationPlan) {
                    break;
                }
                return wrapContent(_jsx(DropletonLegacyMigrationOfferModalView, { currency: accountCurrency, discountedAmount: destinationPlan.interval === "month"
                        ? offer.variant.monthlyCost
                        : offer.variant.totalCost, fullAmount: destinationPlan.amount, interval: destinationPlan.interval, offerEndDate: offerEndDate, offerExpirationDate: offer.expiry, planType: destinationPlan.displayShortName }), isSaving);
            }
        }
        if (state.activeView !== MODAL_STATE.error) {
            setState({
                activeView: MODAL_STATE.error,
                error: {
                    body: "There was a problem finding details for this offer.",
                    title: "Uh oh",
                },
            });
        }
        return null;
    };
    const views = {
        [MODAL_STATE.error]: (_jsx(ModalContent, { maxWidth: DEFAULT_MODAL_WIDTH, children: _jsx(OfferErrorView, { body: state.error.body, onClose: closeModal, title: state.error.title }) })),
        [MODAL_STATE.offer]: (_jsx(ModalContent, { maxWidth: "700px", children: getContent() })),
        [MODAL_STATE.offerAccepted]: (_jsx(ModalContent, { maxWidth: DEFAULT_MODAL_WIDTH, children: _jsx(OfferAcceptedView, { planName: (_k = (_j = (_h = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _h === void 0 ? void 0 : _h.destinationPlan) === null || _j === void 0 ? void 0 : _j.name) !== null && _k !== void 0 ? _k : (promoPlans && ((_l = promoPlans[0]) === null || _l === void 0 ? void 0 : _l.name)) }) })),
    };
    if (!isOfferAvailable && state.activeView !== MODAL_STATE.error) {
        setState({
            activeView: MODAL_STATE.error,
            error: {
                body: "This offer either no longer exists or has expired.",
                title: "Offer not found",
            },
        });
    }
    return (_jsx(GlobalStylesProvider, { isPlatform: true, isRebrand: true, children: _jsx(Modal, { onClosed: isOfferAcceptedView ? () => null : closeModal, children: views[state.activeView] }) }));
};

// @zapier/upsells@5.0.1 (c64b11b3c)
